/**
 * Límite global de subida de archivos en admin (antes de enviar a la API).
 * Evita errores de Tomcat (FileSizeLimitExceededException) con mensajes de UX.
 */
export const DEFAULT_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export const DEFAULT_MAX_UPLOAD_MB = 5;

/** Mensaje estándar para modales warning. */
export function maxUploadSizeErrorMessage(
  maxBytes: number = DEFAULT_MAX_UPLOAD_BYTES,
): string {
  const mb = maxBytes / (1024 * 1024);
  const label = Number.isInteger(mb) ? String(mb) : mb.toFixed(1);
  return `El archivo no debe superar los ${label} MB`;
}

/**
 * @returns mensaje de error o `null` si el archivo es válido / vacío.
 */
export function validateMaxUploadSize(
  file: File | null | undefined,
  maxBytes: number = DEFAULT_MAX_UPLOAD_BYTES,
): string | null {
  if (!file) return null;
  if (file.size > maxBytes) {
    return maxUploadSizeErrorMessage(maxBytes);
  }
  return null;
}

/**
 * Valida varios files (selección múltiple o lista de adjuntos).
 */
export function validateMaxUploadSizes(
  files: Array<File | null | undefined>,
  maxBytes: number = DEFAULT_MAX_UPLOAD_BYTES,
): string | null {
  for (const file of files) {
    const err = validateMaxUploadSize(file, maxBytes);
    if (err) return err;
  }
  return null;
}

/**
 * Valida ítems de formulario con `File` local y/o `fileSize` conocido del API.
 */
export function validateUploadItemsMaxSize(
  items: Array<{ file?: File | null; fileSize?: number | null }>,
  maxBytes: number = DEFAULT_MAX_UPLOAD_BYTES,
): string | null {
  for (const item of items) {
    const size =
      item.file instanceof File
        ? item.file.size
        : typeof item.fileSize === 'number'
          ? item.fileSize
          : 0;
    if (size > maxBytes) {
      return maxUploadSizeErrorMessage(maxBytes);
    }
  }
  return null;
}

/**
 * Extrae el primer File de un event change de input type=file
 * y limpia el input si no es válido (para permitir re-selección).
 */
export function pickFileFromInputEvent(
  event: Event,
  maxBytes: number = DEFAULT_MAX_UPLOAD_BYTES,
): { file: File | null; error: string | null } {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files?.[0] ?? null;
  if (!file) {
    return { file: null, error: null };
  }
  const error = validateMaxUploadSize(file, maxBytes);
  if (error && input) {
    input.value = '';
  }
  return { file: error ? null : file, error };
}

export { formatFileSize } from './format-file-size.utils';
